import React from "react";
import { getLocale, t } from "@auto/services";
import { GUI_LANGUAGE_CHANGE_EVENT } from "@auto/events/Gui";

import Alert from "@mui/material/Alert";

import EChart from "@src/reporting/components/EChart";
import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import { reportingResourceKey } from "@src/reporting/model/reportingFilters.mjs";
import { sexLabel } from "@src/reporting/model/reportingFormatters.mjs";
import { loadRegisteredVoterSexDistribution } from "@src/reporting/services/reportingApi.mjs";

import styles from "@src/reporting/Reporting.module.styl";

const useReportingLocale = () => {
  const [locale, setLocale] = React.useState(() => getLocale());

  React.useEffect(() => {
    const onLanguageChange = (language) => setLocale(language || getLocale());
    GUI_LANGUAGE_CHANGE_EVENT.subscribe(onLanguageChange);
    return () => GUI_LANGUAGE_CHANGE_EVENT.unsubscribe(onLanguageChange);
  }, []);

  return locale;
};

const RegisteredVoterSexDistributionWidget = ({ filter, refreshToken = 0 }) => {
  const locale = useReportingLocale();
  const resource = useReportingResource(
    () => loadRegisteredVoterSexDistribution(filter),
    {
      key: reportingResourceKey("registered-voter-sex", filter),
      refreshToken,
      initialData: [],
    },
  );

  const rows = (resource.data || []).filter((row) => {
    const key = String(row?.key || "").trim().toUpperCase();
    return key === "M" || key === "F";
  });
  const option = React.useMemo(() => ({
    tooltip: { trigger: "item" },
    legend: { bottom: 0 },
    series: [{
      type: "pie",
      radius: ["42%", "68%"],
      center: ["50%", "45%"],
      label: {
        show: true,
        formatter: "{b}: {c}",
      },
      data: rows.map((row) => ({
        name: sexLabel(row.key),
        value: row.count,
      })),
    }],
  }), [rows, locale]);

  return (
    <div className={styles.innerChart}>
      <h3>{t("reporting.sexDistribution")}</h3>
      {resource.error ? <Alert severity="error">{resource.error}</Alert> : null}
      {!resource.error ? (
        <EChart option={option} loading={resource.loading} className={styles.mediumChart} />
      ) : null}
    </div>
  );
};

export default RegisteredVoterSexDistributionWidget;
